import { useEffect, useState } from 'react';
import { withNavBar } from '../../shared/NavBar/NavBar';
import './SavedObservations.scss';
import AgGridWrapper from '../../../shared/Grid/AgGridWrapper';
import { GridApi } from 'ag-grid-community';
import { AttachFile, Download, Pencil, ImageDownload, BusySign } from '../../../constants/Icons';
import Toast from '../../../shared/Notifications/Toast';
import SavedObservationsApiHelpers from './SavedObservationsApiHelpers';
import i18n from 'i18n-js';
import Localization from '../../../shared/Localization';
import sharedHistory from '../../../shared/History';
import { RouteNames } from '../../../shared/enums/RouteNames';
import { ThunkDispatch } from 'redux-thunk';
import { SetObservation } from '../../../store/actions/observation/ObservationAction';
import { connect } from 'react-redux';
import {
  IObservationServiceObj,
} from '../../../shared/apiServices/IApiInterfaces';
import Carousel from './Carousel';
import { ReduxStore } from '../../../store/reducers';
import { Modal } from '../../../shared/Modal/Modal';
import { SetColumnSortSettings, SetColumnFilterSettings } from '../../../store/actions/setting/SettingAction';
import { typePropGetter } from './saveObbservationHelper';
import { Preloader } from '../../../shared/Preloader/Preloader';
import PhotoAPIHelpers from '../../shared/Photo/PhotoAPIHelpers';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

interface ISavedObservatonsProps {
  setObservationObject: (obsObject: IObservationServiceObj) => void;
  observationObj: IObservationServiceObj;
  setColumnSortSettings: (sortSettings: any[]) => void;
  sortSettings: [];
  setColumnFilterSettings: (filterSettings: {}) => void;
  filterSettings: {};
}

const SavedObservations = (props: ISavedObservatonsProps) => {
  const [local, setLocal] = useState<typeof i18n | null>(null);
  const [gridApi, setGridApi] = useState<GridApi>({} as GridApi);
  const [observations, setObservations] = useState<IObservationServiceObj[]>([]);
  const [modal, setModal] = useState<IModal>();
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);

  useEffect(() => {
    fetchTextData();
    loadObservations();
  }, []);

  const fetchTextData = async () => {
    const newLocal = await Localization('en');
    setLocal(newLocal);
  };

  const loadObservations = async () => {
    try {
      setIsLoading(true);
      const observations: IObservationServiceObj[] =
        await SavedObservationsApiHelpers.getAllObservations() || [];
      setObservations(observations);
    } catch (error) {
      Toast.show('Error Getting Observations');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };


  const numberSort = (num1: number, num2: number) => num1 - num2;

  const actions = (params: any): JSX.Element => {

    const data = params.data as IObservationServiceObj;

    const editAction = () => {
      props.setObservationObject(data);
      sharedHistory.push(RouteNames.EditObservations);
    };

    const viewImages = () => {
      props.setObservationObject(data);
      if (data.photoUrls) modal?.open();
      else Toast.show('No images available');
    };

    return (
      <span data-testid={`ObsActionGrid-${data.id}`}>
        <a className="action-icon" onClick={editAction}>
          {Pencil}
        </a>
        {data.photoUrls?.length > 0 && <a className="action-icon" onClick={viewImages} data-testid={`ObsAttachBtnGrid-${data.id}`}>
          {AttachFile}
        </a>}
      </span>
    );
  };

  const columns = [
    { headerName: 'Location', field: 'locationName' },
    { headerName: 'Name', field: 'habitatName' },
    {
      headerName: 'Date',
      field: 'date',
      filter: 'agDateColumnFilter',
      valueFormatter: (params: any) =>
        new Date(params.data.date).toLocaleDateString(undefined, {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        }),
    },
    { headerName: 'Type', field: 'observationType' },
    {
      headerName: '# of Eggs',
      field: 'numEggs',
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: any) => params.data.quantity,
      sortable: true,
      comparator: numberSort
    },
    {
      headerName: '# of Young',
      type: 'numericColumn',
      field: 'numYoung',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: any) =>
        typePropGetter('NumberOfYoung', params.data.habitatTypeProps),
      sortable: true,
      comparator: numberSort
    },
    {
      headerName: '# of Fledged',
      type: 'numericColumn',
      field: 'numFledged',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: any) =>
        typePropGetter('NumberOfFledged', params.data.habitatTypeProps),
      sortable: true,
      comparator: numberSort
    },
    {
      headerName: 'Age (#days)',
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: any) =>
        typePropGetter('YoungAge', params.data.habitatTypeProps),
    },
    {
      headerName: 'Fledge Date',
      valueGetter: (params: any) =>
        typePropGetter('FledgedDate', params.data.habitatTypeProps, 'date'),
    },
    { headerName: 'Species', field: 'wildlifeName' },
    { headerName: 'Notes', field: 'notes' },
    {
      headerName: 'Actions',
      sortable: false,
      filter: false,
      cellRendererFramework: actions,
    },
  ];

  const onGridReady = (params: any) => {
    params.api.sizeColumnsToFit();
    if (props?.sortSettings?.length > 0) {
      params.columnApi.applyColumnState({ state: props.sortSettings });
    } else {
      params.columnApi.applyColumnState({ state: [{ colId: 'date', sort: 'desc' }] })
    }
    if (props.filterSettings && Object.keys(props?.filterSettings)?.length > 0) {
      params.api.setFilterModel(props.filterSettings);
    }
    setGridApi(params.api);
  };

  const onDownloadClick = () => {
    if (gridApi) gridApi.exportDataAsCsv({ fileName: 'observations.csv' });
  };

  const downloadObservationImages = async () => {
    setIsDownloadingImage(true);
    const zip = new JSZip();
    const filteredObservations = observations?.filter((observation) => observation.photoUrls?.length);
    try {
      for (const observation of filteredObservations) {
        const { locationName, photoUrls, date, habitatName, id } = observation;

        let j = 0;
        for (const url of photoUrls) {
          if (url) {
            const split = url.split('/');
            const observationDate = formatDate(new Date(date).toISOString().substring(0, 10));
            const fileName = `${locationName}_${habitatName}_${++j}_${observationDate}.jpg`;
            const response = await PhotoAPIHelpers.getImage(id, split[split.length - 1]);
            zip.file(fileName, response);
          }
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, 'observation_images.zip');
    } catch (error) {
      console.error('Error downloading or compressing the images:', error);
    } finally {
      setIsDownloadingImage(false);
    }
  };

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    return `${month}-${day}-${year}`;
  };

  const onSortChanged = (params: any) => {
    const sortColState = params.columnApi.getColumnState();
    props.setColumnSortSettings(sortColState);
  }

  const onFilterChanged = (params: any) => {
    const filterColState = params.api.getFilterModel();
    props.setColumnFilterSettings(filterColState);
  }

  const getText = (key: string): string => {
    return local?.t(`savedObservations.${key}`) ?? '';
  };

  const galleryModal = (
    <Modal onLoad={setModal}>
      <Carousel></Carousel>
    </Modal>
  );

  return (
    <div className="saved-observations" data-testid="saved-observations">
      <div className="row">
        <h4>{getText('title')}</h4>
        <p>{getText('subTitle')}</p>
      </div>
      <div className="row">
        {isLoading ? (
          <Preloader height="20rem" />
        ) : (
          <>
            <div className="observation-grid-toolbar">
              <div className="observation-grid-toolbar-chips" />
              <div className="observation-grid-toolbar-actions">
                {isDownloadingImage ?
                  <span className="action-icon">
                    {BusySign}
                  </span> :
                  <span className="action-icon" onClick={downloadObservationImages}>
                    {ImageDownload}
                  </span>
                }
                <span className="action-icon" onClick={onDownloadClick}>
                  {Download}
                </span>
              </div>
            </div>
            <AgGridWrapper
              gridName={'ManageUser'}
              rowData={observations}
              columnDefs={columns}
              gridConfig={{
                getRowNodeId: (data: IObservationServiceObj) => {
                  return data.id;
                },
                onGridReady: onGridReady,
                onSortChanged: onSortChanged,
                onFilterChanged: onFilterChanged
              }}
            />
          </>
        )}
      </div>
      {galleryModal}
    </div>
  );
};

const stateToProps = (state: ReduxStore) => {
  return {
    observationObj: state.Observation.ObservationObject,
    sortSettings: state.Setting.ColumnSortSettingObject,
    filterSettings: state.Setting.ColumnFilterSettingObject,
  };
};

const dispatchToProps = (dispatch: ThunkDispatch<{}, {}, any>) => ({
  setObservationObject: (observationObject: IObservationServiceObj) =>
    dispatch(SetObservation(observationObject)),
  setColumnSortSettings: (sortSettings: any[]) => dispatch(SetColumnSortSettings(sortSettings)),
  setColumnFilterSettings: (filterSettings: {}) => dispatch(SetColumnFilterSettings(filterSettings)),
});

export default withNavBar(
  connect(stateToProps, dispatchToProps)(SavedObservations),
  true
);
